import APP_CONFIG from '@/config/app';
import { TokenInterface } from '@/interfaces/TokenInterface';
import { WalletInterface } from '@/interfaces/WalletInterface';
import { WalletTokenInterface } from '@/interfaces/WalletTokenInterface';
import { WalletUnderlyingYieldBearingAssetInterface } from '@/interfaces/WalletUnderlyingYieldBearingAssetInterface';
import { fetchWallets } from '@/services/WalletsService';
import { formatNumberToAmountInUsdString } from '@/utils/formatUtils';
import { logDebug, logError, logInfo } from '@/utils/logger';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '../preauth/AuthProvider';
import { useAuthorization } from './AuthorizationProvider';
export type WalletItem =
  | WalletTokenInterface
  | WalletUnderlyingYieldBearingAssetInterface;
type WalletsContextType = {
  isLoading: boolean;
  isInitialLoading: boolean;
  isWalletAndTransactionRefreshing: boolean;
  isForceFetchInProgress: boolean;
  error: Error | null;
  isUserAuthorized: boolean;
  walletsFound: boolean;
  tokensFound: boolean;
  underlyingYieldBearingAssetsFound: boolean;
  tokensOrUnderlyingYieldBearingAssetsFound: boolean;
  wallets: WalletInterface[];
  tokens: WalletTokenInterface[];
  underlyingYieldBearingAssets: WalletUnderlyingYieldBearingAssetInterface[];
  tokensAndUnderlyingYieldBearingAssets: WalletItem[];
  totalPortfolioValueInUsd: number;
  formattedTotalPortfolioValueInUsd: string;
  totalPortfolioRewardsValueInUsd: number;
  formattedTotalPortfolioRewardsValueInUsd: string;
  underlyingYieldBearingAssetsTotalMarketCap: number;
  underlyingYieldBearingAssetsTvlWeightedReturn: number;
  refreshWalletsAndTransactions: () => Promise<void>;
  refreshWalletsAndTransactionsWithBackoff: () => Promise<void>;
  getDisplayedItems: (noOfItemsToShow: number) => WalletItem[];
  findTokenInWallet: (token: TokenInterface) => WalletItem | null;
  findTokenBySymbolOrAddress: (
    symbol?: string,
    address?: string,
  ) => WalletTokenInterface | null;
  findWalletByAddressOrToken: (
    walletAddress?: string,
    token?: TokenInterface,
  ) => WalletInterface | null;
  getTokensWithPositiveBalance: (networks?: string[]) => WalletTokenInterface[];
  getEligibleTokensForDeposit: () => WalletTokenInterface[];
  getUnderlyingYieldBearingAssetsWithPositiveBalance: () => WalletUnderlyingYieldBearingAssetInterface[];
  hasAssetsDeployedInEarn: () => boolean;
};
const WalletsContext = createContext<WalletsContextType>(
  {} as WalletsContextType,
);
interface UserWalletsProviderProps {
  children: ReactNode;
  staleTime?: number;
  retry?: number;
}
const PROVIDER_NAME = 'UserWalletsProvider';
let userWalletsProviderInstance: any = null;
export const setWalletsForceFetchInProgress = (state: boolean): void => {
  if (userWalletsProviderInstance) {
    userWalletsProviderInstance._setForceFetchInProgress(state);
  }
};
const QUERY_KEY_WALLETS = 'wallets';
const QUERY_KEY_TRANSACTIONS = 'transactions';
export const useWalletsContext = () => useContext(WalletsContext);
export const UserWalletsProvider = ({
  children,
  staleTime = 60 * 60 * 1000,
  retry = 3,
}: UserWalletsProviderProps): JSX.Element => {
  const [isForceFetchInProgress, setIsForceFetchInProgress] = useState(false);
  const _setForceFetchInProgress = useCallback((state: boolean) => {
    setIsForceFetchInProgress(state);
  }, []);
  useEffect(() => {
    userWalletsProviderInstance = { _setForceFetchInProgress };
    return () => {
      userWalletsProviderInstance = null;
    };
  }, [_setForceFetchInProgress]);
  const { user } = useAuth();
  const { isUserAuthorized, setUnauthorized } = useAuthorization();
  const queryClient = useQueryClient();
  const [isManualRefreshTriggered, setIsManualRefreshTriggered] =
    useState(false);
  const [
    isWalletAndTransactionRefreshing,
    setIsWalletAndTransactionRefreshInProgress,
  ] = useState(false);
  const [isInitialLoadAttempted, setIsInitialLoadAttempted] = useState(false);
  const handleUnauthorizedAccess = useCallback(() => {
    logError(
      'Unauthorized access detected. Clearing wallet data.',
      PROVIDER_NAME,
    );
    setUnauthorized();
    queryClient.removeQueries({ queryKey: [QUERY_KEY_WALLETS] });
  }, [queryClient, setUnauthorized]);
  const {
    data: walletsData,
    error,
    isLoading: isQueryLoading,
    isFetching: isQueryFetching,
    refetch: refetchWalletsQuery,
  } = useQuery({
    queryKey: [QUERY_KEY_WALLETS],
    queryFn: fetchWallets,
    enabled: !!user && isUserAuthorized,
    staleTime,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) {
        handleUnauthorizedAccess();
        return false;
      }
      return failureCount < retry;
    },
    placeholderData: (previousData) => previousData,
  });
  useEffect(() => {
    if (walletsData && !isInitialLoadAttempted) {
      logInfo('Initial wallets loaded successfully.', PROVIDER_NAME);
      setIsInitialLoadAttempted(true);
    }
  }, [walletsData, isInitialLoadAttempted]);
  useEffect(() => {
    if (error && !isInitialLoadAttempted) {
      logInfo('Initial wallet load resulted in an error.', PROVIDER_NAME);
      setIsInitialLoadAttempted(true);
    }
  }, [error, isInitialLoadAttempted]);
  const isInitialLoading = !isInitialLoadAttempted && isQueryLoading;
  const isManualLoading = isManualRefreshTriggered && isQueryFetching;
  const isLoading =
    isInitialLoading || isManualLoading || isForceFetchInProgress;
  useEffect(() => {
    if (isForceFetchInProgress) {
      logInfo('Force fetch in progress for wallets', PROVIDER_NAME);
    }
  }, [isForceFetchInProgress]);
  const effectiveInitialLoading = isInitialLoading || isForceFetchInProgress;
  const wallets: WalletInterface[] = walletsData || [];
  const {
    walletsFound,
    tokensFound,
    underlyingYieldBearingAssetsFound,
    tokensOrUnderlyingYieldBearingAssetsFound,
  } = useMemo(() => {
    const hasWallets = wallets.length > 0;
    const hasTokens = wallets.some((w) => w.balances.length > 0);
    const hasUnderlyingAssets = wallets.some(
      (w) => w.underlyingYieldBearingAssetBalances.length > 0,
    );
    return {
      walletsFound: hasWallets,
      tokensFound: hasTokens,
      underlyingYieldBearingAssetsFound: hasUnderlyingAssets,
      tokensOrUnderlyingYieldBearingAssetsFound:
        hasTokens || hasUnderlyingAssets,
    };
  }, [wallets]);
  const {
    totalPortfolioValueInUsd,
    formattedTotalPortfolioValueInUsd,
    totalPortfolioRewardsValueInUsd,
    formattedTotalPortfolioRewardsValueInUsd,
  } = useMemo(() => {
    let totalValue = 0;
    let totalRewards = 0;
    wallets.forEach((wallet) => {
      totalValue += wallet.balances.reduce(
        (sum, token) => sum + token.balanceInUsd,
        0,
      );
      totalValue += wallet.underlyingYieldBearingAssetBalances.reduce(
        (sum, asset) => sum + asset.balanceInUsd,
        0,
      );
      totalRewards += wallet.underlyingYieldBearingAssetBalances.reduce(
        (sum, asset) => sum + (asset.rewardsInUsd || 0),
        0,
      );
    });
    return {
      totalPortfolioValueInUsd: totalValue,
      formattedTotalPortfolioValueInUsd:
        formatNumberToAmountInUsdString(totalValue),
      totalPortfolioRewardsValueInUsd: totalRewards,
      formattedTotalPortfolioRewardsValueInUsd:
        formatNumberToAmountInUsdString(totalRewards),
    };
  }, [wallets]);
  const tokens = useMemo(
    () =>
      wallets
        .flatMap((w) => w.balances)
        .sort((a, b) => b.balanceInUsd - a.balanceInUsd),
    [wallets],
  );
  const underlyingYieldBearingAssets: WalletUnderlyingYieldBearingAssetInterface[] =
    useMemo(
      () =>
        wallets
          .flatMap((w) => w.underlyingYieldBearingAssetBalances)
          .sort((a, b) => b.balanceInUsd - a.balanceInUsd),
      [wallets],
    );
  const tokensAndUnderlyingYieldBearingAssets = useMemo(
    () =>
      [...tokens, ...underlyingYieldBearingAssets].sort(
        (a, b) => b.balanceInUsd - a.balanceInUsd,
      ),
    [tokens, underlyingYieldBearingAssets],
  );
  const getDisplayedItems = useCallback(
    (numberOfItemsToShow: number): WalletItem[] => {
      if (
        numberOfItemsToShow <= 0 ||
        tokensAndUnderlyingYieldBearingAssets.length <= numberOfItemsToShow
      ) {
        return tokensAndUnderlyingYieldBearingAssets;
      }
      return tokensAndUnderlyingYieldBearingAssets.slice(
        0,
        numberOfItemsToShow,
      );
    },
    [tokensAndUnderlyingYieldBearingAssets],
  );
  const findTokenInWallet = useCallback(
    (tokenToFind: TokenInterface): WalletItem | null => {
      const addressLower = tokenToFind.address.toLowerCase();
      const blockchainLower = tokenToFind.blockchain.toLowerCase();
      for (const wallet of wallets) {
        const foundItem = [
          ...wallet.balances,
          ...wallet.underlyingYieldBearingAssetBalances,
        ].find(
          (item) =>
            'address' in item &&
            item.address?.toLowerCase() === addressLower &&
            item.blockchain?.toLowerCase() === blockchainLower,
        );
        if (foundItem) {
          return foundItem;
        }
      }
      return null;
    },
    [wallets],
  );
  const getTokensWithPositiveBalance = useCallback(
    (networks?: string[]): WalletTokenInterface[] => {
      const lowerCaseNetworks = networks?.map((n) => n.toLowerCase());
      return tokens.filter(
        (token) =>
          token.balance > 0 &&
          (!lowerCaseNetworks ||
            lowerCaseNetworks.includes(token.blockchain.toLowerCase())),
      );
    },
    [tokens],
  );
  const getUnderlyingYieldBearingAssetsWithPositiveBalance = useCallback(
    (): WalletUnderlyingYieldBearingAssetInterface[] =>
      underlyingYieldBearingAssets.filter((asset) => asset.balance > 0),
    [underlyingYieldBearingAssets],
  );
  const getEligibleTokensForDeposit = useCallback(
    (): WalletTokenInterface[] => getTokensWithPositiveBalance(),
    [getTokensWithPositiveBalance],
  );
  const hasAssetsDeployedInEarn = useCallback(
    (): boolean =>
      underlyingYieldBearingAssets.some((asset) => asset.balanceInUsd > 0),
    [underlyingYieldBearingAssets],
  );
  const isWalletUnderlyingYieldBearingAsset = (
    item: WalletItem,
  ): item is WalletUnderlyingYieldBearingAssetInterface => {
    return 'tvlUSD' in item;
  };
  const {
    underlyingYieldBearingAssetsTotalMarketCap,
    underlyingYieldBearingAssetsTvlWeightedReturn,
  } = useMemo(() => {
    let totalTvl = 0;
    let weightedReturnSum = 0;
    const relevantAssets = underlyingYieldBearingAssets.filter(
      isWalletUnderlyingYieldBearingAsset,
    );
    if (!relevantAssets.length) {
      return {
        underlyingYieldBearingAssetsTotalMarketCap: 0,
        underlyingYieldBearingAssetsTvlWeightedReturn: 0,
      };
    }
    relevantAssets.forEach((asset) => {
      totalTvl += asset.tvlUSD || 0;
    });
    if (totalTvl === 0) {
      return {
        underlyingYieldBearingAssetsTotalMarketCap: 0,
        underlyingYieldBearingAssetsTvlWeightedReturn: 0,
      };
    }
    relevantAssets.forEach((asset) => {
      const apy = asset.apy || 0;
      const tvl = asset.tvlUSD || 0;
      const weight = tvl / totalTvl;
      weightedReturnSum += apy * weight;
    });
    return {
      underlyingYieldBearingAssetsTotalMarketCap: totalTvl,
      underlyingYieldBearingAssetsTvlWeightedReturn: weightedReturnSum,
    };
  }, [underlyingYieldBearingAssets]);
  const findTokenBySymbolOrAddress = useCallback(
    (symbol?: string, address?: string): WalletTokenInterface | null => {
      if (symbol) {
        const symbolLower = symbol.toLowerCase();
        return (
          tokens.find((t) => t.symbol?.toLowerCase() === symbolLower) || null
        );
      }
      if (address) {
        const addressLower = address.toLowerCase();
        return (
          tokens.find((t) => t.address?.toLowerCase() === addressLower) || null
        );
      }
      return null;
    },
    [tokens],
  );
  const findWalletByAddressOrToken = useCallback(
    (
      walletAddress?: string,
      token?: TokenInterface,
    ): WalletInterface | null => {
      if (walletAddress) {
        const addressLower = walletAddress.toLowerCase();
        return (
          wallets.find((w) => w.address.toLowerCase() === addressLower) || null
        );
      }
      if (token) {
        const tokenAddressLower = token.address.toLowerCase();
        return (
          wallets.find(
            (wallet) =>
              wallet.balances.some(
                (balance) =>
                  balance.address.toLowerCase() === tokenAddressLower,
              ) ||
              wallet.underlyingYieldBearingAssetBalances.some(
                (asset) => asset.address.toLowerCase() === tokenAddressLower,
              ),
          ) || null
        );
      }
      return null;
    },
    [wallets],
  );
  const refreshWalletsAndTransactions = useCallback(async (): Promise<void> => {
    const caller = `${PROVIDER_NAME}.refreshWalletsAndTransactions`;
    if (isManualRefreshTriggered || isQueryFetching) {
      logInfo('Refresh already in progress, skipping.', caller);
      return;
    }
    setIsManualRefreshTriggered(true);
    setIsWalletAndTransactionRefreshInProgress(true);
    logInfo('Starting manual refresh of wallets and transactions.', caller);
    try {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_WALLETS] });
      await refetchWalletsQuery();
      logInfo(
        'Wallets refreshed successfully. Invalidating transactions query.',
        caller,
      );
      logInfo('Invalidating transactions query.', caller);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_TRANSACTIONS] });
      logInfo('Refetching transactions.', caller);
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEY_TRANSACTIONS],
        exact: true,
      });
      logInfo('Wallets and transactions refresh complete.', caller);
    } catch (err) {
      logError(err, caller);
      throw err;
    } finally {
      setIsManualRefreshTriggered(false);
      setIsWalletAndTransactionRefreshInProgress(false);
    }
  }, [
    refetchWalletsQuery,
    isManualRefreshTriggered,
    isQueryFetching,
    queryClient,
  ]);
  const refreshWalletsAndTransactionsWithBackoff = useCallback(
    async (
      sequentialFetchCount: number = APP_CONFIG.NO_OF_TIMES_TO_FETCH_ASSETS_AFTER_YIELD_DEPLOYMENT,
    ): Promise<void> => {
      const caller = `${PROVIDER_NAME}.refreshWalletsAndTransactionsWithBackoff`;
      const config = APP_CONFIG.WALLETS_AND_TRANSACTIONS_REFRESH;
      if (isWalletAndTransactionRefreshing) {
        logInfo('Backoff refresh already in progress, skipping.', caller);
        return;
      }
      setIsManualRefreshTriggered(true);
      setIsWalletAndTransactionRefreshInProgress(true);
      logInfo('Starting refresh with backoff.', caller);
      try {
        const initialWalletDataString = walletsData
          ? JSON.stringify(walletsData)
          : null;
        let walletDataChanged = false;
        let attempt = 0;
        for (let i = 0; i < sequentialFetchCount; i++) {
          attempt++;
          logInfo(
            `Starting sequential fetch pair ${i + 1}/${sequentialFetchCount}.`,
            caller,
          );
          logInfo(`Refreshing wallets: sequential attempt ${attempt}.`, caller);
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY_WALLETS] });
          const { data: refreshedWalletData } = await refetchWalletsQuery();
          const refreshedWalletDataString = refreshedWalletData
            ? JSON.stringify(refreshedWalletData)
            : null;
          if (
            initialWalletDataString &&
            refreshedWalletDataString !== initialWalletDataString
          ) {
            logInfo(
              `Wallet data changed after sequential attempt ${attempt}.`,
              caller,
            );
            walletDataChanged = true;
          } else if (!initialWalletDataString && refreshedWalletDataString) {
            logInfo(
              `Wallet data loaded after sequential attempt ${attempt}.`,
              caller,
            );
            walletDataChanged = true;
          } else {
            logDebug(
              `Wallet data unchanged after sequential attempt ${attempt}.`,
              caller,
            );
          }
          logInfo(
            `Refreshing transactions: sequential attempt ${attempt}.`,
            caller,
          );
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY_TRANSACTIONS] });
          await queryClient.refetchQueries({
            queryKey: [QUERY_KEY_TRANSACTIONS],
            exact: true,
          });
          if (i < sequentialFetchCount - 1) {
            const sequentialDelayMs = config.INITIAL_DELAY_IN_MS;
            logDebug(
              `Waiting ${sequentialDelayMs}ms before next sequential fetch pair.`,
              caller,
            );
            await new Promise((resolve) =>
              setTimeout(resolve, sequentialDelayMs),
            );
          }
        }
        if (!walletDataChanged) {
          let currentDelayMs = config.INITIAL_DELAY_IN_MS;
          while (attempt < config.MAX_ATTEMPTS && !walletDataChanged) {
            const jitterFactor =
              1 + (Math.random() * 2 - 1) * config.JITTER_FACTOR;
            const delayWithJitterMs = Math.floor(currentDelayMs * jitterFactor);
            logDebug(
              `Waiting ${delayWithJitterMs}ms before wallet refresh attempt ${
                attempt + 1
              }/${config.MAX_ATTEMPTS}.`,
              caller,
            );
            await new Promise((resolve) =>
              setTimeout(resolve, delayWithJitterMs),
            );
            logInfo(
              `Refreshing wallets: backoff attempt ${attempt + 1}/${
                config.MAX_ATTEMPTS
              }.`,
              caller,
            );
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY_WALLETS] });
            const { data: refreshedWalletData } = await refetchWalletsQuery();
            const refreshedWalletDataString = refreshedWalletData
              ? JSON.stringify(refreshedWalletData)
              : null;
            if (
              initialWalletDataString &&
              refreshedWalletDataString !== initialWalletDataString
            ) {
              logInfo(
                `Wallet data changed after backoff attempt ${
                  attempt + 1
                }. Stopping backoff.`,
                caller,
              );
              walletDataChanged = true;
            } else if (!initialWalletDataString && refreshedWalletDataString) {
              logInfo(
                `Wallet data loaded after backoff attempt ${
                  attempt + 1
                }. Stopping backoff.`,
                caller,
              );
              walletDataChanged = true;
            } else {
              logDebug(
                `Wallet data unchanged after backoff attempt ${attempt + 1}.`,
                caller,
              );
            }
            currentDelayMs *= config.BACKOFF_FACTOR;
            attempt++;
          }
          if (!walletDataChanged && attempt === config.MAX_ATTEMPTS) {
            logInfo(
              `Max wallet refresh attempts (${config.MAX_ATTEMPTS}) reached without data change.`,
              caller,
            );
          }
          if (walletDataChanged || attempt > sequentialFetchCount) {
            logInfo(
              'Wallet refresh phase complete. Refreshing transactions one last time.',
              caller,
            );
            try {
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEY_TRANSACTIONS],
              });
              const transactionRefreshPromise = queryClient.refetchQueries({
                queryKey: [QUERY_KEY_TRANSACTIONS],
                exact: true,
              });
              const timeoutPromise = new Promise<void>((_, reject) => {
                setTimeout(() => {
                  reject(
                    new Error(
                      `Transaction refresh timed out after ${config.TRANSACTION_REFRESH_TIMEOUT_IN_MS}ms`,
                    ),
                  );
                }, config.TRANSACTION_REFRESH_TIMEOUT_IN_MS);
              });
              await Promise.race([transactionRefreshPromise, timeoutPromise]);
              logInfo('Transactions refresh complete.', caller);
            } catch (transactionError) {
              logError(
                transactionError,
                `${caller} - Final Transaction refresh failed or timed out`,
              );
            }
          }
        }
        logInfo('Wallets and transactions refresh complete.', caller);
      } catch (err) {
        logError(err, caller);
        throw err;
      } finally {
        setIsManualRefreshTriggered(false);
        setIsWalletAndTransactionRefreshInProgress(false);
        logInfo('Refresh with backoff finished.', caller);
      }
    },
    [
      refetchWalletsQuery,
      walletsData,
      queryClient,
      isWalletAndTransactionRefreshing,
    ],
  );
  const contextValue = useMemo(
    () => ({
      isLoading,
      isInitialLoading: effectiveInitialLoading,
      isWalletAndTransactionRefreshing,
      isForceFetchInProgress,
      error: isForceFetchInProgress ? null : error || null,
      isUserAuthorized,
      walletsFound: isForceFetchInProgress ? true : walletsFound,
      tokensFound: isForceFetchInProgress ? true : tokensFound,
      underlyingYieldBearingAssetsFound: isForceFetchInProgress
        ? true
        : underlyingYieldBearingAssetsFound,
      tokensOrUnderlyingYieldBearingAssetsFound: isForceFetchInProgress
        ? true
        : tokensOrUnderlyingYieldBearingAssetsFound,
      wallets: isForceFetchInProgress && wallets.length === 0 ? [] : wallets,
      tokens: isForceFetchInProgress && tokens.length === 0 ? [] : tokens,
      underlyingYieldBearingAssets:
        isForceFetchInProgress && underlyingYieldBearingAssets.length === 0
          ? []
          : underlyingYieldBearingAssets,
      tokensAndUnderlyingYieldBearingAssets:
        isForceFetchInProgress &&
        tokensAndUnderlyingYieldBearingAssets.length === 0
          ? []
          : tokensAndUnderlyingYieldBearingAssets,
      totalPortfolioValueInUsd,
      formattedTotalPortfolioValueInUsd,
      totalPortfolioRewardsValueInUsd,
      formattedTotalPortfolioRewardsValueInUsd,
      underlyingYieldBearingAssetsTotalMarketCap,
      underlyingYieldBearingAssetsTvlWeightedReturn,
      refreshWalletsAndTransactions,
      refreshWalletsAndTransactionsWithBackoff,
      getDisplayedItems,
      findTokenInWallet,
      findTokenBySymbolOrAddress,
      findWalletByAddressOrToken,
      getTokensWithPositiveBalance,
      getEligibleTokensForDeposit,
      getUnderlyingYieldBearingAssetsWithPositiveBalance,
      hasAssetsDeployedInEarn,
    }),
    [
      isLoading,
      effectiveInitialLoading,
      isWalletAndTransactionRefreshing,
      isForceFetchInProgress,
      error,
      isUserAuthorized,
      walletsFound,
      tokensFound,
      underlyingYieldBearingAssetsFound,
      tokensOrUnderlyingYieldBearingAssetsFound,
      wallets,
      tokens,
      underlyingYieldBearingAssets,
      tokensAndUnderlyingYieldBearingAssets,
      totalPortfolioValueInUsd,
      formattedTotalPortfolioValueInUsd,
      totalPortfolioRewardsValueInUsd,
      formattedTotalPortfolioRewardsValueInUsd,
      underlyingYieldBearingAssetsTotalMarketCap,
      underlyingYieldBearingAssetsTvlWeightedReturn,
      refreshWalletsAndTransactions,
      refreshWalletsAndTransactionsWithBackoff,
      getDisplayedItems,
      findTokenInWallet,
      findTokenBySymbolOrAddress,
      findWalletByAddressOrToken,
      getTokensWithPositiveBalance,
      getEligibleTokensForDeposit,
      getUnderlyingYieldBearingAssetsWithPositiveBalance,
      hasAssetsDeployedInEarn,
    ],
  );
  useEffect(() => {
    if (error) {
      logError(error, PROVIDER_NAME);
    }
  }, [error]);
  return (
    <WalletsContext.Provider value={contextValue}>
      {children}
    </WalletsContext.Provider>
  );
};
